import { logStorage } from '../logger';

export interface LoginStorageData {
  // Access and refresh tokens for authentication
  accessToken: string | undefined;
  refreshToken: string | undefined;
  managerId: string | undefined;
  trainerId: string | undefined;
  memberId: string | undefined;
  isImpersonating: boolean | undefined;
}

const SAVED_LOGIN_DATA_KEY = 'motivision-login-user';

export const setLoginStorageData = (loginStorageData: LoginStorageData) => {
  localStorage.setItem(SAVED_LOGIN_DATA_KEY, JSON.stringify(loginStorageData));
  logStorage('loginStorage:set', {
    hasAccessToken: Boolean(loginStorageData.accessToken),
    hasRefreshToken: Boolean(loginStorageData.refreshToken),
    hasManager: Boolean(loginStorageData.managerId),
    hasTrainer: Boolean(loginStorageData.trainerId),
    hasMember: Boolean(loginStorageData.memberId),
  });
};

export const getLoginStorageData = (): LoginStorageData | null => {
  const loginData = localStorage.getItem(SAVED_LOGIN_DATA_KEY);
  if (!loginData) {
    logStorage('loginStorage:get:empty');
    return null;
  }
  try {
    const parsed = JSON.parse(loginData);
    logStorage('loginStorage:get', {
      hasAccessToken: Boolean(parsed?.accessToken),
      hasRefreshToken: Boolean(parsed?.refreshToken),
      hasManager: Boolean(parsed?.managerId),
      hasTrainer: Boolean(parsed?.trainerId),
      hasMember: Boolean(parsed?.memberId),
    });
    return parsed;
  } catch (error) {
    logStorage('loginStorage:get:parse-error', { error });
    return null;
  }
};

export const clearLoginStorageData = (): void => {
  localStorage.setItem(SAVED_LOGIN_DATA_KEY, '');
  logStorage('loginStorage:clear');
};

export const hasValidLoginStorageData = (
  loginStorageData: LoginStorageData | null,
): boolean => {
  if (!loginStorageData) return false;

  // Impersonation sessions bypass token requirement
  if (loginStorageData.isImpersonating) {
    return Boolean(
      loginStorageData.managerId ||
      loginStorageData.trainerId ||
      loginStorageData.memberId,
    );
  }

  return Boolean(
    loginStorageData.accessToken && loginStorageData.refreshToken,
  );
};

/**
 * Get the current access token for API Authorization headers
 */
export const getAccessToken = (): string | null => {
  const loginData = getLoginStorageData();
  return loginData?.accessToken ?? null;
};

export const getLogoutIdentity = (): { 
  type: 'member' | 'trainer' | 'manager'; 
  id: string 
} | null => {
  const loginData = getLoginStorageData();
  if (!loginData) return null;

  // Trainer takes priority when multiple role IDs exist
  if (loginData.trainerId) {
    return { type: 'trainer', id: loginData.trainerId };
  }
  if (loginData.managerId) {
    return { type: 'manager', id: loginData.managerId };
  }
  if (loginData.memberId) {
    return { type: 'member', id: loginData.memberId };
  }
  return null;
};
